import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from '@/components/ui/menubar';
import { useAuth } from '@/features/auth/auth.hooks';
import useMenu from '@/hooks/use-menu';
import {
  ChevronDown,
  Eye,
  LogOut,
  MapPin,
  Terminal,
  UserRound,
} from 'lucide-react';
import MapPinIcon from '@/assets/icons/map-pin.svg';
import { useFilterStore } from '@/stores/filter.store';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { PopoverClose } from '@radix-ui/react-popover';
import type { IBranch } from '@/features/user-access/user-access.types';
import { useBranchList } from '@/hooks/use-branch-list';
import { useProfile } from '@/hooks/use-profile';
import { AvatarImage } from '@radix-ui/react-avatar';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { ViewAsDialog } from '@/features/auth/components/view-as-dialog';

function CmsHeader() {
  const location = useFilterStore(s => s.location);
  const setLocation = useFilterStore(s => s.setLocation);

  const { currentRoute, currentParent } = useMenu();
  const { logout } = useAuth();
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const [viewAsOpen, setViewAsOpen] = useState(false);

  // Buka halaman edit profil = form edit user (employee-management) untuk user yang login.
  const goToProfile = () => {
    if (profile?.id) {
      navigate(`/user-access/employee-management/form/${profile.id}`);
    }
  };

  // Superadmin Console is reserved for the Admin role for now.
  const isAdmin = profile?.role?.name?.toLowerCase() === 'admin';

  /**
   * The picker below is drawn on Today and the Planner only, and only an admin
   * picks from the fetched list — everyone else picks from their own profile.
   * The query is gated on both, so the other pages (the booking form included)
   * no longer pay for a list they never show. `useBranchList` is shared, so on
   * the two pages that do show it this is the same request the planner's
   * default-location lookup makes.
   */
  const showsLocationPicker = ['today', 'planner'].includes(
    currentRoute?.key ?? ''
  );
  const { branches } = useBranchList({
    enabled: showsLocationPicker && isAdmin,
  });

  return (
    <>
      <header className="hidden w-full items-center justify-between rounded-2xl p-6 md:flex">
        <h4 className="text-text-primary text-2xl font-bold">
          {/* A tab page is titled by its section ("Company"); the tab strip
              below already names the tab. */}
          {currentParent?.tabs ? currentParent.label : currentRoute?.label}
        </h4>

        {showsLocationPicker && (
          <div>
            <Popover>
              <PopoverTrigger className="cursor-pointer" asChild>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <img src={MapPinIcon} alt="map-pin" className="size-5" />
                    <h1 className="text-2xl font-medium">{location?.name}</h1>
                  </div>
                  <ChevronDown className="size-4" />
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="max-h-96 overflow-hidden overflow-y-auto p-1"
              >
                <ul>
                  {profile?.role?.name?.toLowerCase() === 'admin'
                    ? branches.map(branch => (
                        <PopoverClose
                          key={branch?.id}
                          onClick={() => setLocation(branch)}
                          asChild
                        >
                          <li className="text-text-secondary hover:bg-neutral-80 flex cursor-pointer items-center gap-1.5 rounded p-2 text-sm">
                            <MapPin className="size-4" />
                            {branch?.name}
                          </li>
                        </PopoverClose>
                      ))
                    : profile?.branch
                        ?.filter(branch => branch?.status !== 'suspended')
                        ?.map(branch => (
                          <PopoverClose
                            key={branch?.id}
                            onClick={() => setLocation(branch as IBranch)}
                            asChild
                          >
                            <li className="text-text-secondary hover:bg-neutral-80 flex cursor-pointer items-center gap-1.5 rounded p-2 text-sm">
                              <MapPin className="size-4" />
                              {branch?.name}
                            </li>
                          </PopoverClose>
                        ))}
                </ul>
              </PopoverContent>
            </Popover>
          </div>
        )}

        <div className="flex h-full items-center space-x-4">
          <Menubar className="border-0 bg-transparent shadow-none">
            <MenubarMenu>
              <MenubarTrigger asChild className="rounded bg-transparent">
                <Button size="sm" variant="ghost">
                  <Avatar>
                    <AvatarImage
                      src={profile?.profilePicture?.url ?? undefined}
                    />
                    <AvatarFallback>{profile?.name?.at(0)}</AvatarFallback>
                  </Avatar>
                  {profile?.name ?? ''}
                </Button>
              </MenubarTrigger>

              <MenubarContent align="end">
                {isAdmin && (
                  <>
                    <MenubarItem onClick={() => navigate('/console')}>
                      Superadmin Console
                      <MenubarShortcut>
                        <Terminal />
                      </MenubarShortcut>
                    </MenubarItem>
                    <MenubarItem onClick={() => setViewAsOpen(true)}>
                      View as role
                      <MenubarShortcut>
                        <Eye />
                      </MenubarShortcut>
                    </MenubarItem>
                    <MenubarSeparator />
                  </>
                )}
                <MenubarItem onClick={goToProfile}>
                  Profile
                  <MenubarShortcut>
                    <UserRound />
                  </MenubarShortcut>
                </MenubarItem>
                <MenubarSeparator />
                <MenubarItem onClick={logout}>
                  Logout
                  <MenubarShortcut>
                    <LogOut />
                  </MenubarShortcut>
                </MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>
        </div>
      </header>

      <header className="relative flex items-center gap-6 bg-neutral-100 px-6 py-[18px] md:hidden">
        <div className="flex w-full items-center justify-between">
          <h5 className="text-text-primary pl-6 text-xl font-bold">
            👋 Hi {profile?.name}
          </h5>
          <Menubar className="border-0 bg-transparent shadow-none">
            <MenubarMenu>
              <MenubarTrigger asChild className="rounded bg-transparent">
                <Button size="sm" variant="ghost">
                  <Avatar>
                    <AvatarImage
                      src={profile?.profilePicture?.url ?? undefined}
                    />
                    <AvatarFallback>{profile?.name?.at(0)}</AvatarFallback>
                  </Avatar>
                </Button>
              </MenubarTrigger>

              <MenubarContent align="end">
                {isAdmin && (
                  <>
                    <MenubarItem onClick={() => navigate('/console')}>
                      Superadmin Console
                      <MenubarShortcut>
                        <Terminal />
                      </MenubarShortcut>
                    </MenubarItem>
                    <MenubarItem onClick={() => setViewAsOpen(true)}>
                      View as role
                      <MenubarShortcut>
                        <Eye />
                      </MenubarShortcut>
                    </MenubarItem>
                    <MenubarSeparator />
                  </>
                )}
                <MenubarItem onClick={goToProfile}>
                  Profile
                  <MenubarShortcut>
                    <UserRound />
                  </MenubarShortcut>
                </MenubarItem>
                <MenubarSeparator />
                <MenubarItem onClick={logout}>
                  Logout
                  <MenubarShortcut>
                    <LogOut />
                  </MenubarShortcut>
                </MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>
        </div>
      </header>

      {isAdmin && (
        <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />
      )}
    </>
  );
}

export { CmsHeader };
